import React, { useEffect, useState } from "react";
import { Table, Card, Button, Tag, Empty, Spin, Popconfirm } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePen, faTrash, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
// REDUX
import {
  useLazyGetOrganizationsQuery,
  useDeleteOrganizationMutation,
} from "../../../redux/organizations/organizationAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
// FLATBUFFER DISPLAY
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
// STORE
import { RootState } from "../../../app/store";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// AUTHORIZATION
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// COMPONENTS
import OrganizationDraftsTableActions from "./OrganizationDraftsTableActions";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

export default function OrganizationDraftsPage() {
  const { theme, globalOrganizationId } = useSelector((state: RootState) => state.home);
  const navigate = useNavigate();
  // Reads SCREEN_PERMISSION["drafts"] -- the same declaration Layout.tsx's
  // sidebar link reads. Previously this page had no self-check of its own
  // (see the "drafts" comment in screenPermissions.ts); the sidebar hiding
  // the link was the only enforcement, so a direct URL visit bypassed it
  // for any authenticated user regardless of permission.
  const allowed = useScreenPermission("drafts");

  const [drafts, setDrafts] = useState<OrganizationInterface[]>([]);
  // The server pages the list (`page`/`limit`, with the full count in `total`), so the table
  // asks for one page at a time instead of rendering the first N and dropping the rest.
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });

  const [triggerGetOrgs, { data, isLoading, isFetching }] = useLazyGetOrganizationsQuery();

  const [triggerDeleteOrg, { isLoading: isDeleting, isSuccess: isDeleteSuccess }] =
    useDeleteOrganizationMutation();

  const fetchDrafts = async (page = pagination.current, limit = pagination.pageSize) => {
    const token = await GETJwtToken();
    triggerGetOrgs({
      page,
      limit,
      token,
      parentId: globalOrganizationId,
      searchAndFilterString: "",
      statusFilter: "DRAFT",
    });
  };

  useEffect(() => {
    if (globalOrganizationId) {
      fetchDrafts();
    }
  }, [globalOrganizationId, pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (data && data.data) {
      setDrafts(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (isDeleteSuccess) {
      // Deleting the last row of a page leaves that page empty: step back one.
      if (drafts.length === 1 && pagination.current > 1) {
        setPagination((p) => ({ ...p, current: p.current - 1 }));
      } else {
        fetchDrafts();
      }
    }
  }, [isDeleteSuccess]);

  const handleDelete = async (id: string) => {
    const token = await GETJwtToken();
    triggerDeleteOrg({ id, token });
  };

  const handleResume = (orgId: string) => {
    navigate(`/organization-management/setup-wizard?orgId=${orgId}`);
  };

  const handleDeleteRecord = (record: OrganizationInterface) => {
    handleDelete(formatUuidT(record.id));
  };

  const handleResumeRecord = (record: OrganizationInterface) => {
    handleResume(formatUuidT(record.id));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text: OrgType) => (
        <Tag color={text === OrgType.ORG ? "blue" : "orange"}>{orgTypeLabel(text)}</Tag>
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: OrganizationInterface) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<FontAwesomeIcon icon={faArrowRight} />}
            onClick={() => handleResumeRecord(record)}
          >
            Resume
          </Button>
          <Popconfirm
            title="Delete this draft?"
            onConfirm={() => handleDeleteRecord(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<FontAwesomeIcon icon={faTrash} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
    {
      // The row's ⋯ menu: Export downloads the whole draft as one binary export.
      title: "",
      key: "more",
      align: "right" as const,
      width: 48,
      render: (_: unknown, record: OrganizationInterface) => (
        <OrganizationDraftsTableActions selectedItem={record} />
      ),
    },
  ];

  return (
    <div className={`layout__contentPage__${theme}`}>
      {allowed ? (
        <Card style={{ margin: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: 0 }}>
              <FontAwesomeIcon icon={faFilePen} style={{ marginRight: 8 }} />
              Organization Drafts
            </h2>
            <Button
              type="primary"
              onClick={() => navigate("/organization-management/setup-wizard")}
            >
              + New Organization
            </Button>
          </div>
          <Spin spinning={isLoading || isFetching || isDeleting}>
            {drafts.length === 0 && !isLoading && !isFetching ? (
              <Empty description="No draft organizations" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                dataSource={drafts}
                className="my-custom-table"
                columns={columns}
                rowKey={(record) => formatUuidT(record.id)}
                size="small"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: data?.total ?? drafts.length,
                  showSizeChanger: true,
                  pageSizeOptions: PAGE_SIZE_OPTIONS,
                  showTotal: (total: number) => `${total} drafts`,
                }}
                onChange={(next) =>
                  setPagination({
                    current: next.current ?? 1,
                    pageSize: next.pageSize ?? DEFAULT_PAGE_SIZE,
                  })
                }
                rowClassName={(_, index) => getTableRowClass(index, theme)}
              />
            )}
          </Spin>
        </Card>
      ) : (
        <UnauthorizedPage />
      )}
    </div>
  );
}
